import { useEffect, useRef, useState } from 'react'
import { BUILD_ID } from '@/lib/build'

const POLL_MS = 5 * 60_000

/**
 * Notices a deploy while the tab stays open.
 *
 * One customer and their crew leave ServicePilot open for days, so a deploy reaches them only when
 * something forces a reload. This fetches `/version.json`, which every build writes, and
 * compares it with the id compiled into the running bundle.
 *
 * It checks on mount, every five minutes, and whenever the tab becomes visible again. The
 * visibility check is the one that usually fires: a laptop that slept overnight wakes up stale.
 *
 * Returns true once, then stops polling. There is nothing more to learn after that.
 */
export function useNewVersion(): boolean {
  const [stale, setStale] = useState(false)
  // The effect must not re-subscribe every time `stale` changes, so read it through a ref.
  const staleRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      if (cancelled || staleRef.current) return
      try {
        // `no-store` plus the timestamp defeats both the browser cache and Cloudflare, which
        // would otherwise keep serving the version file from before the deploy.
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) return
        const { buildId } = (await res.json()) as { buildId?: string }
        // A missing or identical id means nothing to do. In dev there is no version.json at all.
        if (!buildId || buildId === BUILD_ID) return
        staleRef.current = true
        if (!cancelled) setStale(true)
      } catch {
        // Offline, or the file is mid-deploy. Try again on the next tick.
      }
    }

    void check()
    const timer = window.setInterval(() => void check(), POLL_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') void check() }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return stale
}
